import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useVisibleNodeIds, buildParentMap } from '../../../features/labelFilter'
import { useSelectionFromStorage } from '../../../features/report'
import type { MindMapRecord } from '@bandos/shared'

export function useReportVisibility(
  mindmap: MindMapRecord | null,
  nodeLabelMap: Map<string, string[]>,
  selectedFilterLabels: string[],
  mindmapId?: string
) {
  // Build parent map for filtering using shared utility
  const parentMap = useMemo(
    () => (mindmap ? buildParentMap(mindmap.content.edges) : new Map<string, string>()),
    [mindmap]
  )

  // Use shared visibility logic
  const { shouldDisplayNode } = useVisibleNodeIds({
    nodeLabelMap,
    parentMap,
    allNodeIds: mindmap?.content.nodes.map((n) => n.id) ?? [],
    selectedFilterLabels,
  })

  // Node-based filter from URL (from clicking "Export" on a node)
  const [searchParams, setSearchParams] = useSearchParams()
  const nodeFilterId = searchParams.get('nodeFilter')

  // Compute visible set for node-based filter: selected node + all ancestors
  const nodeFilteredNodeIds = useMemo(() => {
    if (!nodeFilterId) return null
    const visible = new Set<string>([nodeFilterId])
    let currentId = nodeFilterId
    const visited = new Set<string>([currentId])
    let parentId = parentMap.get(currentId)
    while (parentId !== undefined) {
      if (visited.has(parentId)) break
      visited.add(parentId)
      visible.add(parentId)
      currentId = parentId
      parentId = parentMap.get(currentId)
    }
    return visible
  }, [nodeFilterId, parentMap])

  // Export selection filter from sessionStorage (from export sidebar branch selection)
  const storageKey = mindmapId ? `report-selection-${mindmapId}` : undefined
  const {
    selectionIds: exportSelectionIds,
    hasSelection: hasExportSelection,
    clearSelection: clearExportSelection,
  } = useSelectionFromStorage(storageKey)

  // Effective visibility: node filter > export selection > label filter
  const effectiveShouldDisplayNode = useCallback(
    (nodeId: string): boolean => {
      if (nodeFilteredNodeIds !== null) return nodeFilteredNodeIds.has(nodeId)
      if (exportSelectionIds !== null) return exportSelectionIds.has(nodeId)
      return shouldDisplayNode(nodeId)
    },
    [nodeFilteredNodeIds, exportSelectionIds, shouldDisplayNode]
  )

  return {
    effectiveShouldDisplayNode,
    nodeFilterId,
    setSearchParams,
    hasExportSelection,
    clearExportSelection,
    exportSelectionIds,
  }
}
